// components/sections/contact/ContactSection.tsx
"use client";

import clsx from "clsx";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { CONTACT_CHANNELS } from "@/data/contact-page";
import { ContactChannelItem } from "./ContactChannelItem";
import { ContactForm } from "./ContactForm";
import type { ContactFormValues } from "@/hooks/useContactForm";

interface ContactSectionProps {
  onSubmit?: (values: ContactFormValues) => Promise<void>;
}

const revealClass = (visible: boolean, fromX: "left" | "right") =>
  clsx(
    "transition-all duration-700 ease-out will-change-transform",
    visible
      ? "opacity-100 translate-x-0"
      : fromX === "left"
      ? "opacity-0 -translate-x-8"
      : "opacity-0 translate-x-8"
  );

export function ContactSection({ onSubmit }: ContactSectionProps) {
  const left = useScrollReveal<HTMLDivElement>();
  const right = useScrollReveal<HTMLDivElement>({ rootMargin: "0px 0px -10% 0px" });

  return (
    <section
      id="contact"
      className="relative w-full bg-white px-5 py-24 sm:px-9 sm:py-32 lg:px-14"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent" />

      <div className="mx-auto grid w-full max-w-[1200px] gap-16 lg:grid-cols-[380px_1fr] lg:gap-20">
        {/* Left column */}
        <div ref={left.ref} className={revealClass(left.isVisible, "left")}>
          <p className="mb-4 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[3px] text-amber-700">
            <span className="inline-block h-px w-6 bg-amber-400" />
            Get in touch
          </p>

          <h2 className="text-[clamp(2rem,4vw,3.2rem)] font-extrabold uppercase leading-[0.96] tracking-tight text-stone-900">
            Start a
            <br />
            <span className="text-amber-700">conversation</span>
          </h2>

          <p className="mt-5 text-[15px] leading-relaxed text-stone-500">
            Reach the right team directly, or use the form and we&apos;ll route
            it for you within one working day.
          </p>

          <ul className="mt-8 flex flex-col gap-1">
            {CONTACT_CHANNELS.map((channel, i) => (
              <ContactChannelItem key={channel.email} channel={channel} index={i} />
            ))}
          </ul>
        </div>

        {/* Right column — form */}
        <div
          ref={right.ref}
          className={clsx(
            revealClass(right.isVisible, "right"),
            "rounded-3xl border border-stone-200 bg-stone-50 p-7 sm:p-10 shadow-sm"
          )}
        >
          <p className="mb-6 text-[11px] font-semibold uppercase tracking-[3px] text-stone-400">
            Send us a message
          </p>
          <ContactForm onSubmit={onSubmit} />
        </div>
      </div>
    </section>
  );
}